import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { useTranslation } from 'react-i18next';

const Earnings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { earnings, statements, isLoading, paypalInfo, savePayPalInfo } = useData();
  const { t } = useTranslation();
  
  const [paypalForm, setPaypalForm] = useState({
    full_name: '',
    paypal_email: ''
  });
  const [isSavingPaypal, setIsSavingPaypal] = useState(false);

  // PayPal bilgileri yüklendiğinde formu doldur
  useEffect(() => {
    if (paypalInfo) {
      setPaypalForm({
        full_name: paypalInfo.full_name,
        paypal_email: paypalInfo.paypal_email
      });
    }
  }, [paypalInfo]);

  const handlePayPalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!paypalForm.full_name || !paypalForm.paypal_email) {
      alert(t('earnings.paypalValidationError'));
      return;
    }

    setIsSavingPaypal(true);
    
    try {
      const success = await savePayPalInfo(paypalForm);
      if (success) {
        alert(t('earnings.paypalSaveSuccess'));
      } else {
        alert(t('earnings.paypalSaveError'));
      }
    } catch (error) {
      console.error('PayPal bilgileri kaydetme hatası:', error);
      alert(t('earnings.paypalSaveError'));
    } finally {
      setIsSavingPaypal(false);
    }
  };

  const totalEarnings = earnings.reduce((sum, earning) => sum + parseFloat(earning.revenue), 0);
  const currentMonthEarnings = earnings
    .filter(e => e.month === new Date().toISOString().slice(0, 7))
    .reduce((sum, earning) => sum + parseFloat(earning.revenue), 0);
  
  const hasEarnings = earnings.length > 0;

  const tabs = [
    { id: 'overview', name: t('earnings.overview'), icon: '📊' },
    { id: 'earnings', name: t('earnings.earningsDetail'), icon: '💰' },
    { id: 'statements', name: t('earnings.statements'), icon: '📄' },
    { id: 'payouts', name: t('earnings.payouts'), icon: '🏦' },
    { id: 'tax', name: t('earnings.taxForm'), icon: '📋' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Loading State */}
            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="glass-card animate-pulse">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-white/20 rounded-xl"></div>
                      <div className="ml-4 space-y-2">
                        <div className="w-24 h-4 bg-white/20 rounded"></div>
                        <div className="w-16 h-6 bg-white/20 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !hasEarnings && (
              <div className="glass-card text-center py-12">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  {t('earnings.noEarningsYet')}
                </h3>
                <p className="text-white/70 mb-8 max-w-md mx-auto">
                  {t('earnings.noEarningsDescription')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                    {t('earnings.uploadTrackButton')}
                  </button>
                  <button className="px-6 py-3 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300">
                    {t('earnings.howItWorksButton')}
                  </button>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            {!isLoading && hasEarnings && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-green-400 to-green-600 rounded-xl">
                      <span className="text-2xl">💰</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white/70">{t('earnings.totalEarnings')}</p>
                      <p className="text-2xl font-bold text-white">₺{totalEarnings.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl">
                      <span className="text-2xl">📈</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white/70">{t('earnings.currentMonth')}</p>
                      <p className="text-2xl font-bold text-white">₺{currentMonthEarnings.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="glass-card">
                  <div className="flex items-center">
                    <div className="p-3 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl">
                      <span className="text-2xl">🎵</span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white/70">{t('admin.totalStreamsLabel')}</p>
                      <p className="text-2xl font-bold text-white">
                        {earnings.reduce((sum, e) => sum + e.streams, 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Chart Placeholder */}
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
{t('admin.monthlyChart')}
              </h3>
              <div className="h-64 bg-white/5 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
                <div className="text-center">
                  <span className="text-4xl mb-2 block">📊</span>
                  <p className="text-white/70">{t('admin.chartHere')}</p>
                  <p className="text-sm text-white/50">{t('admin.chartIntegration')}</p>
                </div>
              </div>
            </div>

            {/* Recent Earnings */}
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
{t('admin.recentEarnings')}
              </h3>
              <div className="space-y-3">
                {earnings.slice(0, 5).map((earning) => (
                  <div key={earning.id} className="py-4 px-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-medium text-white">{earning.track_name}</p>
                          {earning.is_manual_entry && (
                            <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded-full border border-orange-500/30">
                              {t('earnings.manual')}
                            </span>
                          )}
                        </div>
                        
                        {/* Multiple Platforms Support */}
                        <div className="flex flex-wrap gap-1 mb-1">
                          {earning.platforms && earning.platforms.length > 0 ? (
                            earning.platforms.map((platform, index) => (
                              <span key={index} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                                {platform}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs bg-gray-500/20 text-gray-300 px-2 py-1 rounded">
                              {earning.platform}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-white/70">{earning.month}</p>
                        {earning.notes && (
                          <p className="text-xs text-white/60 mt-1 italic">"{earning.notes}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="flex items-center space-x-1">
                          <p className="font-medium text-white">
                            {earning.currency === 'USD' ? '$' : earning.currency === 'EUR' ? '€' : '₺'}
                            {earning.revenue}
                          </p>
                          {earning.currency && earning.currency !== 'TRY' && (
                            <span className="text-xs text-white/50">{earning.currency}</span>
                          )}
                        </div>
                        <p className="text-sm text-white/70">{earning.streams?.toLocaleString() || 0} {t('earnings.streams')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'earnings':
        return (
          <div className="space-y-6">
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {t('admin.detailedTable')}
              </h3>
              
              {/* Loading State */}
              {isLoading && (
                <div className="animate-pulse">
                  <div className="h-4 bg-white/20 rounded w-full mb-4"></div>
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="h-12 bg-white/10 rounded mb-2"></div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!isLoading && !hasEarnings && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {t('earnings.noDetailedEarnings')}
                  </h3>
                  <p className="text-white/70 mb-6">
                    {t('earnings.noEarningsDescription')}
                  </p>
                </div>
              )}

              {/* Data Table */}
              {!isLoading && hasEarnings && (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-white/20">
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('admin.trackName')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('admin.platformName')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('earnings.month')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('earnings.streams')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('earnings.revenue')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('earnings.type')}
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-white/80 uppercase tracking-wider">
                          {t('earnings.notes')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {earnings.map((earning) => (
                        <tr key={earning.id} className="hover:bg-white/5 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            <div className="flex items-center space-x-2">
                              <span>{earning.track_name}</span>
                              {earning.is_manual_entry && (
                                <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded-full border border-orange-500/30">
                                  {t('earnings.manual')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-white/70">
                            <div className="flex flex-wrap gap-1">
                              {earning.platforms && earning.platforms.length > 0 ? (
                                earning.platforms.map((platform, index) => (
                                  <span key={index} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded border border-blue-500/30">
                                    {platform}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs bg-gray-500/20 text-gray-300 px-2 py-1 rounded border border-gray-500/30">
                                  {earning.platform}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                            {earning.month}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                            {earning.streams?.toLocaleString() || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            <div className="flex items-center space-x-1">
                              <span>
                                {earning.currency === 'USD' ? '$' : earning.currency === 'EUR' ? '€' : '₺'}
                                {earning.revenue}
                              </span>
                              {earning.currency && earning.currency !== 'TRY' && (
                                <span className="text-xs text-white/50">{earning.currency}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">
                            {earning.is_manual_entry ? (
                              <span className="flex items-center space-x-1">
                                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                                <span>{t('earnings.manualEntry')}</span>
                              </span>
                            ) : (
                              <span className="flex items-center space-x-1">
                                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                <span>{t('earnings.automaticEntry')}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-white/70">
                            {earning.notes ? (
                              <div className="max-w-xs truncate" title={earning.notes}>
                                "{earning.notes}"
                              </div>
                            ) : (
                              <span className="text-white/40">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );

      case 'statements':
        return (
          <div className="space-y-6">
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
{t('earnings.monthlyStatements')}
              </h3>
              <div className="space-y-4">
                {statements.map((statement, index) => (
                  <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-white">{statement.month}</h4>
                      <span className="text-sm text-white/70">
                        {t('admin.total')}: ₺{Object.values(statement.platform_breakdown)
                          .reduce((sum, amount) => sum + parseFloat(amount), 0)
                          .toFixed(2)
                        }
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(statement.platform_breakdown).map(([platform, amount]) => (
                        <div key={platform} className="flex justify-between p-2 bg-white/5 rounded-lg">
                          <span className="text-sm text-white/70">{platform}:</span>
                          <span className="text-sm font-medium text-white">₺{amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'payouts':
        return (
          <div className="space-y-6">
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {t('earnings.payoutInfoTitle')}
              </h3>

              {/* PayPal Payment Method */}
              <div className="mb-8">
                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-blue-500/20 rounded-xl mr-4">
                      <svg className="w-8 h-8 text-blue-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h8.418c2.508 0 4.578.087 5.459 1.849.449.899.449 2.448-.449 4.597-.449 1.349-1.347 2.698-2.695 3.598-1.798 1.199-4.058 1.349-6.566 1.349H8.37l-.9 4.297c-.449 2.099-1.798 3.898-4.057 3.898zm6.416-14.335c.9 0 1.348-.45 1.798-1.798.45-1.349.45-2.098-.9-2.098h-2.248l-.9 3.896h2.25z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-white">{t('earnings.paypalPayment')}</h4>
                      <p className="text-blue-200 text-sm">{t('earnings.paypalDescription')}</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handlePayPalSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          {t('earnings.fullName')} *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          placeholder="John Doe"
                          value={paypalForm.full_name}
                          onChange={(e) => setPaypalForm(prev => ({ ...prev, full_name: e.target.value }))}
                          required
                        />
                        <p className="text-xs text-blue-200 mt-1">{t('earnings.paypalNameNote')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          {t('earnings.paypalEmail')} *
                        </label>
                        <input
                          type="email"
                          className="input-field"
                          placeholder="john@paypal.com"
                          value={paypalForm.paypal_email}
                          onChange={(e) => setPaypalForm(prev => ({ ...prev, paypal_email: e.target.value }))}
                          required
                        />
                        <p className="text-xs text-blue-200 mt-1">{t('earnings.paypalEmailNote')}</p>
                      </div>
                    </div>
                    
                    {paypalInfo && (
                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-300 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <div className="text-sm text-green-200">
                            <p className="font-medium">{t('earnings.paypalSaved')}</p>
                            <p className="text-xs mt-1">{t('earnings.paypalLastUpdated')}: {new Date(paypalInfo.updated_at).toLocaleDateString('tr-TR')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-300 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">{t('earnings.paypalImportant')}</p>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            <li>{t('earnings.paypalNote1')}</li>
                            <li>{t('earnings.paypalNote2')}</li>
                            <li>{t('earnings.paypalNote3')}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end pt-4 border-t border-blue-500/20">
                      <button 
                        type="submit" 
                        disabled={isSavingPaypal}
                        className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                      >
                        {isSavingPaypal ? t('earnings.saving') : t('earnings.savePaypalInfo')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Alternative Payment Methods Notice */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <svg className="w-6 h-6 text-orange-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-lg font-semibold text-white">{t('earnings.alternativePayments')}</h4>
                </div>
                <p className="text-white/70 text-sm">
                  {t('earnings.alternativePaymentsNote')}
                </p>
                <div className="mt-4 flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-white/60">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    <span>{t('earnings.bankTransfer')}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-white/60">
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                    <span>{t('earnings.otherMethods')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'tax':
        return (
          <div className="space-y-6">
            <div className="glass-card">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('earnings.taxFormTitle')}
              </h3>
              <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-300">
                      {t('earnings.taxInfo')}
                    </h3>
                    <div className="mt-2 text-sm text-blue-200">
                      <p>{t('earnings.taxRequirement')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t('earnings.idNumber')}
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="12345678901"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {t('earnings.taxNumber')}
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="1234567890"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('earnings.taxOfficeField')}
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder={t('earnings.taxOffice')}
                  />
                </div>
                
                <div className="flex justify-end pt-4 border-t border-white/20">
                  <button type="submit" className="btn-primary">
                    {t('earnings.saveTaxForm')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">{t('earnings.title')}</h1>
        <p className="mt-1 text-sm text-white/70">
          {t('earnings.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-2">
        <nav className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white shadow-lg'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default Earnings; 